import * as Yup from "yup";
import { messages } from "../../../utils/messages";
import {
  createAdditionalProfileValidationSchema,
  getAdditionalProfileFieldRules,
  getDependentFieldRules,
  validateAdditionalProfileSync,
  normalizeYupPath,
} from "./userFormAdditionalProfileValidation";

const { validation } = messages;

const isAdminRole = (role) => Number(role) === 2;

export function createUserFormValidationSchema(t, mode, additionalProfileSchema) {
  return Yup.object().shape({
    email: Yup.string()
      .email(validation.invalidEmail)
      .max(100, validation.maximumCharacters)
      .required(validation.requiredField),
    password:
      mode === "edit"
        ? Yup.string().notRequired()
        : Yup.string()
            .min(6, t("app.auth.validationPasswordMin"))
            .max(100, t("app.auth.validationMaxChars"))
            .required(t("app.auth.validationPasswordRequired")),
    name: Yup.string()
      .min(3, validation.invalidFullName)
      .max(150, validation.invalidFullName)
      .required(validation.requiredField),
    empStartDate:
      mode === "edit"
        ? Yup.date()
            .transform((value, originalValue) =>
              originalValue === "" || originalValue === null || originalValue === undefined
                ? undefined
                : value
            )
            .when("role", {
              is: isAdminRole,
              then: (schema) => schema.notRequired(),
              otherwise: (schema) => schema.required(validation.requiredField),
            })
        : Yup.date().required(validation.requiredField),
    officeId: Yup.number().nullable().required(validation.requiredField),
    departmentId: Yup.number().nullable().optional(),
    designationId: Yup.string().nullable().optional(),
    employmentTypeId: Yup.string().nullable().optional(),
    teamIds: Yup.array().of(Yup.string()).optional(),
    additionalProfile: additionalProfileSchema.nullable(),
    leaveYearStart: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue === null || originalValue === undefined ? null : value
      )
      .when("role", {
        is: isAdminRole,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.required(validation.requiredField),
      }),
    role: Yup.number().nullable().required(validation.requiredField),
    userLeaveSettings: Yup.array()
      .of(
        Yup.object().shape({
          leaveTypeId: Yup.mixed().required(),
          name: Yup.string(),
          value: Yup.mixed()
            .test("is-number-or-empty", "Must be a number or empty", (value) =>
              value === "" || (typeof value === "number" && value >= 0)
            )
            .when(["active", "deducted"], {
              is: (active, deducted) => active === true && deducted === true,
              then: (schema) => schema.required(validation.requiredField),
              otherwise: (schema) => schema.notRequired(),
            }),
          type: Yup.string().oneOf(["Days", "Hours"]),
          active: Yup.bool(),
        })
      )
      .optional(),
    isFowardLoginDetails: Yup.bool(),
    isActive: Yup.bool().default(true),
    isLoginDisabled: Yup.bool().default(false),
    approvalId: Yup.string()
      .transform((value) => (value === null || value === undefined ? "" : String(value)))
      .when("role", {
        is: isAdminRole,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.required(validation.requiredField),
      }),
  });
}

function getNestedValue(obj, path) {
  if (!path) return undefined;
  const normalized = normalizeYupPath(path);
  return normalized.split(".").reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
}

function getNestedError(errors, path) {
  const value = getNestedValue(errors, path);
  if (typeof value === "string") return value;
  return null;
}

function getNestedTouched(touched, path) {
  const value = getNestedValue(touched, path);
  return Boolean(value);
}

function flattenValidationErrors(err) {
  if (!err) return {};
  if (!err.inner || err.inner.length === 0) {
    const path = normalizeYupPath(err.path);
    return path ? { [path]: err.message } : {};
  }
  return err.inner.reduce((acc, item) => {
    const path = normalizeYupPath(item.path);
    if (path) acc[path] = item.message;
    return acc;
  }, {});
}

export function validateUserFormSync(schema, values) {
  try {
    schema.validateSync(values, { abortEarly: false });
    return {};
  } catch (err) {
    return flattenValidationErrors(err);
  }
}

export function getUserFormFieldSections(t, mode, isAdminInEditMode, values = null) {
  const personalFields = [
    {
      id: "fullName",
      touchPath: "name",
      label: t("app.users.validationFieldFullName"),
      requirement: t("app.users.validationRuleFullName"),
    },
    {
      id: "email",
      touchPath: "email",
      label: t("app.users.validationFieldEmail"),
      requirement: t("app.users.validationRuleEmail"),
    },
  ];

  if (mode !== "edit") {
    personalFields.push({
      id: "password",
      touchPath: "password",
      label: t("app.users.validationFieldPassword"),
      requirement: t("app.users.validationRulePassword"),
    });
  }

  const employmentFields = [
    {
      id: "assignRole",
      touchPath: "role",
      label: t("app.users.validationFieldRole"),
      requirement: t("app.users.validationRuleRole"),
    },
    {
      id: "workplace",
      touchPath: "officeId",
      label: t("app.users.validationFieldWorkplace"),
      requirement: t("app.users.validationRuleWorkplace"),
    },
    {
      id: "department",
      touchPath: "departmentId",
      label: t("app.users.validationFieldDepartment"),
      requirement: t("app.users.validationRuleDepartment"),
    },
    {
      id: "empStartDate",
      touchPath: "empStartDate",
      label: t("app.users.validationFieldEmpStartDate"),
      requirement: isAdminInEditMode
        ? t("app.users.validationRuleEmpStartDateAdmin")
        : t("app.users.validationRuleEmpStartDate"),
    },
    {
      id: "leaveApprover",
      touchPath: "approvalId",
      label: t("app.users.approverManagerLabel"),
      requirement: isAdminInEditMode
        ? t("app.users.validationRuleApproverAdmin")
        : t("app.users.validationRuleApprover"),
    },
    {
      id: "designationId",
      touchPath: "designationId",
      label: t("app.users.designation"),
      requirement: t("app.users.validationRuleOptional"),
    },
    {
      id: "employmentTypeId",
      touchPath: "employmentTypeId",
      label: t("app.users.employmentType"),
      requirement: t("app.users.validationRuleOptional"),
    },
    {
      id: "teamIds",
      touchPath: "teamIds",
      label: t("app.sidebar.organizationTeams"),
      requirement: t("app.users.validationRuleTeams"),
    },
    {
      id: "leaveYearStarts",
      touchPath: "leaveYearStart",
      label: t("app.users.validationFieldLeaveYearStart"),
      requirement: isAdminInEditMode
        ? t("app.users.validationRuleLeaveYearStartAdmin")
        : t("app.users.validationRuleLeaveYearStart"),
    },
  ];

  const additionalFields = [
    ...getAdditionalProfileFieldRules(t).map((field) => ({
      ...field,
      touchPath: `additionalProfile.${field.id}`,
    })),
    ...getDependentFieldRules(t, values?.additionalProfile?.dependents),
  ];

  return [
    {
      id: "personal",
      title: t("app.users.validationSectionPersonal"),
      fields: personalFields,
    },
    {
      id: "employment",
      title: t("app.users.validationSectionEmployment"),
      fields: employmentFields,
    },
    {
      id: "additional",
      title: t("app.users.additionalInformationTitle"),
      fields: additionalFields,
    },
  ];
}

export function getUserFormValidationIssues(
  values,
  formikErrors,
  touched,
  schema,
  sections,
  additionalProfileSchema,
  options = {}
) {
  const { mode = "create", submitCount = 0 } = options;
  const runLiveValidation = mode !== "edit" || submitCount > 0;
  const liveErrors = runLiveValidation ? validateUserFormSync(schema, values) : {};
  // Dependents are required as soon as a row is added, so always validate them live.
  const additionalLive = validateAdditionalProfileSync(
    additionalProfileSchema,
    values?.additionalProfile
  );
  const issues = [];

  sections.forEach((section) => {
    section.fields.forEach((field) => {
      const fieldIsTouched = getNestedTouched(touched, field.touchPath);
      const isDependentField = field.touchPath?.includes(".dependents.");
      const canUseFormikError =
        isDependentField || mode !== "edit" || submitCount > 0 || fieldIsTouched;
      let message = canUseFormikError ? getNestedError(formikErrors, field.touchPath) : null;

      if (isDependentField || runLiveValidation) {
        message = message || liveErrors[field.touchPath];
        if (!message && field.touchPath.startsWith("additionalProfile.")) {
          const key = field.touchPath.replace("additionalProfile.", "");
          message = additionalLive[key] || additionalLive[normalizeYupPath(field.yupPath || key)] || null;
        }
      }
      if (message) {
        issues.push({ ...field, sectionId: section.id, message });
      }
    });
  });

  return issues;
}

export function scrollToFormField(fieldId) {
  const el = document.getElementById(fieldId);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  if (typeof el.focus === "function") {
    el.focus({ preventScroll: true });
  }
}
